import {
  loadSpeex,
  SpeexWorkletNode,
  loadRnnoise,
  RnnoiseWorkletNode,
  NoiseGateWorkletNode,
} from "@sapphi-red/web-noise-suppressor";

import speexWorkletUrl from "@sapphi-red/web-noise-suppressor/speexWorklet.js?url";
import noiseGateWorkletUrl from "@sapphi-red/web-noise-suppressor/noiseGateWorklet.js?url";
import rnnoiseWorkletUrl from "@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url";
import speexWasmUrl from "@sapphi-red/web-noise-suppressor/speex.wasm?url";
import rnnoiseWasmUrl from "@sapphi-red/web-noise-suppressor/rnnoise.wasm?url";
import rnnoiseSimdWasmUrl from "@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url";

type NoiseSuppressorType = "disabled" | "speex" | "rnnoise" | "noiseGate";

interface CleanAudioOptions {
  type?: NoiseSuppressorType;
  monitorToSpeaker?: boolean;
}

let ctx: AudioContext | null = null;
let setupPromise: Promise<void> | null = null;
let speexWasmBinary: ArrayBuffer | null = null;
let rnnoiseWasmBinary: ArrayBuffer | null = null;

const getAudioContext = () => {
  if (!ctx) {
    ctx = new AudioContext({
      sampleRate: 48000,
      latencyHint: "interactive",
    });
  }

  return ctx;
};

const setupNoiseSuppressors = async (audioContext: AudioContext) => {
  if (!setupPromise) {
    setupPromise = (async () => {
      const [speexBinary, rnnoiseBinary] = await Promise.all([
        loadSpeex({ url: speexWasmUrl }),
        loadRnnoise({
          url: rnnoiseWasmUrl,
          simdUrl: rnnoiseSimdWasmUrl,
        }),
      ]);

      speexWasmBinary = speexBinary;
      rnnoiseWasmBinary = rnnoiseBinary;

      await Promise.all([
        audioContext.audioWorklet.addModule(speexWorkletUrl),
        audioContext.audioWorklet.addModule(noiseGateWorkletUrl),
        audioContext.audioWorklet.addModule(rnnoiseWorkletUrl),
      ]);
    })();
  }

  await setupPromise;
};

const createProcessor = (
  audioContext: AudioContext,
  type: NoiseSuppressorType,
) => {
  if (type === "speex" && speexWasmBinary) {
    return new SpeexWorkletNode(audioContext, {
      wasmBinary: speexWasmBinary,
      maxChannels: 2,
    });
  }

  if (type === "noiseGate") {
    return new NoiseGateWorkletNode(audioContext, {
      openThreshold: -50,
      closeThreshold: -60,
      holdMs: 90,
      maxChannels: 2,
    });
  }

  if (type === "rnnoise" && rnnoiseWasmBinary) {
    return new RnnoiseWorkletNode(audioContext, {
      wasmBinary: rnnoiseWasmBinary,
      maxChannels: 2,
    });
  }

  return null;
};

export async function cleanAudioRNNoise(
  rawStream: MediaStream,
  options: CleanAudioOptions = {},
): Promise<MediaStream> {
  const rawAudioTrack = rawStream.getAudioTracks()[0];
  if (!rawAudioTrack) return new MediaStream();

  try {
    const audioContext = getAudioContext();
    await audioContext.resume();
    await setupNoiseSuppressors(audioContext);

    const type = options.type || "rnnoise";
    const source = audioContext.createMediaStreamSource(rawStream);
    const gain = new GainNode(audioContext, { gain: 1 });
    const destination = audioContext.createMediaStreamDestination();
    const processor = createProcessor(audioContext, type);

    if (processor) {
      source.connect(processor);
      processor.connect(gain);
    } else {
      source.connect(gain);
    }

    gain.connect(destination);

    // Do not monitor judge/local mic to loudspeaker by default. Self playback
    // defeats browser echo cancellation and creates an audible echo loop.
    if (options.monitorToSpeaker) {
      gain.connect(audioContext.destination);
    }

    const cleanTrack = destination.stream.getAudioTracks()[0];
    if (cleanTrack) {
      (cleanTrack as any).__sourceDeviceId =
        rawAudioTrack.getSettings().deviceId;
      (cleanTrack as any).__sourceLabel = rawAudioTrack.label;
    }

    const cleanup = () => {
      source.disconnect();
      processor?.disconnect();
      (processor as any)?.destroy?.();
      gain.disconnect();
    };

    rawAudioTrack.addEventListener("ended", cleanup, { once: true });
    cleanTrack?.addEventListener("ended", cleanup, { once: true });

    return destination.stream;
  } catch (error) {
    console.error(
      "Noise suppressor setup failed, using raw audio stream.",
      error,
    );
    return new MediaStream([rawAudioTrack]);
  }
}
